import { uniqBy } from "lodash"
import { ExceptionInputs, SortableInput } from "./types"

const normalizeCircuitExceptionName = (exceptionName = "") =>
  exceptionName.replace(/\s+/g, " ").trim()

const numberedCircuitRegex = /\b(?:ckt|circuit)\s*\d+[a-z]?\b/i
const genericCircuitRegex = /\b(?:ckt|circuit)\b/i

const getGenericCircuitExceptionName = (exceptionName = "") =>
  normalizeCircuitExceptionName(exceptionName).replace(
    numberedCircuitRegex,
    (match) => match.replace(/\s+\d+[a-z]?\b/i, "")
  )

const filterGenericCircuitExceptions = (exceptions: any[] = []): any[] => {
  const numberedCircuitExceptionNames = new Set(
    exceptions
      .filter((item: any) => numberedCircuitRegex.test(item?.exceptionName || ""))
      .map((item: any) => getGenericCircuitExceptionName(item?.exceptionName || ""))
  )

  return exceptions.filter((item: any) => {
    const exceptionName = item?.exceptionName || ""
    const normalizedExceptionName = normalizeCircuitExceptionName(exceptionName)
    const isGenericCircuitException =
      genericCircuitRegex.test(exceptionName) &&
      !numberedCircuitRegex.test(exceptionName)

    return !(
      isGenericCircuitException &&
      numberedCircuitExceptionNames.has(normalizedExceptionName)
    )
  })
}

export const updateExceptions = (
  exceptionInputs: ExceptionInputs,
  componentRelatedProperties: any,
  selectedEquipmentType: any,
  isAll = false
) => {
  let data: any[] =
    exceptionInputs?.reduce(
    (exceptionList: any, analyticItem: any) => {
      const { equipmentType, equipmentComponent, exceptionInputs } =
        analyticItem
      const circuitInstances: string[] =
        componentRelatedProperties?.circuitInstances || []
      const compressorInstances: string[] =
        componentRelatedProperties?.compressorInstances || []
      const parsedExceptions = exceptionInputs?.flatMap(
        (exceptionInput: any) => {
          const parsedException = JSON.parse(exceptionInput)
          const modifierRegex = new RegExp(/[X$#@!%^*&*[\]]{2}/)
          const circuitRegex = new RegExp(
            /(([Cc][Kk][Tt])|([Cc][iI][rR][cC][uU][iI][tT]))[a-zA-z]*/
          )
          const compressorRegex = new RegExp(/([Cc][Oo][mM][pP])[a-zA-z]*/)
          if (parsedException?.uiExpList) {
            const expList = Object.values(
              parsedException?.uiExpList
            ) as Array<string | undefined>
            const expListParsedException = expList?.flatMap((uiExpListValue) => {
              const uiExpListName = uiExpListValue || ""
              if (
                circuitRegex?.test(uiExpListName) &&
                modifierRegex?.test(uiExpListName)
              ) {
                return circuitInstances?.map((instance) => {
                  const newExceptionName = uiExpListName?.replace(
                    modifierRegex,
                    instance
                  )
                  return {
                    exceptionName: `${newExceptionName} ${
                      equipmentComponent && isAll
                        ? `(${equipmentComponent})`
                        : equipmentType && isAll
                        ? `(${equipmentType})`
                        : ""
                    }`,
                    exceptionKey: newExceptionName,
                    equipmentType: equipmentType || selectedEquipmentType?.value
                  }
                })
              } else if (
                compressorRegex?.test(uiExpListName) &&
                modifierRegex?.test(uiExpListName)
              ) {
                return compressorInstances?.map((instance) => {
                  const newExceptionName = uiExpListName?.replace(
                    modifierRegex,
                    instance
                  )
                  return {
                    exceptionName: `${newExceptionName} ${
                      equipmentComponent && isAll
                        ? `(${equipmentComponent})`
                        : equipmentType && isAll
                        ? `(${equipmentType})`
                        : ""
                    }`,
                    exceptionKey: newExceptionName,
                    equipmentType: equipmentType || selectedEquipmentType?.value
                  }
                })
              } else {
                return [
                  {
                    exceptionName: `${uiExpListName} ${
                      equipmentComponent && isAll
                        ? `(${equipmentComponent})`
                        : equipmentType && isAll
                        ? `(${equipmentType})`
                        : ""
                    }`,
                    exceptionKey: uiExpListName,
                    equipmentType: equipmentType || selectedEquipmentType?.value
                  }
                ]
              }
            })
            return [...expListParsedException]
          } else {
            if (
              circuitRegex?.test(parsedException?.uiExpName) &&
              modifierRegex?.test(parsedException?.uiExpName)
            ) {
              return circuitInstances?.map((instance) => {
                const newExceptionName = parsedException?.uiExpName?.replace(
                  modifierRegex,
                  instance
                )
                return {
                  exceptionName: `${newExceptionName} ${
                    equipmentComponent && isAll
                      ? `(${equipmentComponent})`
                      : equipmentType && isAll
                      ? `(${equipmentType})`
                      : ""
                  }`,
                  exceptionKey: newExceptionName,
                  equipmentType: equipmentType || selectedEquipmentType?.value
                }
              })
            } else if (
              compressorRegex?.test(parsedException?.uiExpName) &&
              modifierRegex?.test(parsedException?.uiExpName)
            ) {
              return compressorInstances?.map((instance) => {
                const newExceptionName = parsedException?.uiExpName?.replace(
                  modifierRegex,
                  instance
                )
                return {
                  exceptionName: `${newExceptionName} ${
                    equipmentComponent && isAll
                      ? `(${equipmentComponent})`
                      : equipmentType && isAll
                      ? `(${equipmentType})`
                      : ""
                  }`,
                  exceptionKey: newExceptionName,
                  equipmentType: equipmentType || selectedEquipmentType?.value
                }
              })
            } else {
              return [
                {
                  exceptionName: `${parsedException?.uiExpName} ${
                    equipmentComponent && isAll
                      ? `(${equipmentComponent})`
                      : equipmentType && isAll
                      ? `(${equipmentType})`
                      : ""
                  }`,
                  exceptionKey: parsedException?.uiExpName,
                  equipmentType: equipmentType || selectedEquipmentType?.value
                }
              ]
            }
          }
        }
      )
      return [...exceptionList, ...parsedExceptions]
    },
    [] as any[]
  ) || []
  data = uniqBy(data, (item: any) => item?.exceptionName?.toLowerCase()).filter(
    (val: any) => val.exceptionKey
  )
  data = filterGenericCircuitExceptions(data)
  data = data.sort((a: SortableInput, b: SortableInput) => {
    const exceptionNameA = a.exceptionName ?? ""
    const exceptionNameB = b.exceptionName ?? ""

    return exceptionNameA > exceptionNameB
      ? 1
      : exceptionNameB > exceptionNameA
      ? -1
      : 0
  })

  return data
}

